import React, { useRef, useEffect, useState } from "react"
import { graphql } from "gatsby"
import Flink from '~/components/shared/flink'
import { useTheme } from '~/contexts/ThemeContext'
import { notBlank, jnstyls, imgSize } from '~/utils/helpers'

import { Seo } from '~/components/shared/seo'
import { CarouselTimeline } from '~/components/carousel/carousel_timeline'
import { CarouselFixed } from '~/components/carousel/carousel_fixed'
import { CarouselWindow } from '~/components/carousel/carousel_window'
import VideoNoControl from '~/components/video/no-controls'
import VideoControls from '~/components/video/controls'
import TilePage from '~/components/tile/tile-page'

import * as styles from "~/styles/pages/factory.module.scss"
import { tiles } from "~/styles/plp/plp.module.scss"
import { aria_btn } from "~/styles/app.module.scss"


const Factory = ({ data: { json } }) => {
  const { isMobile } = useTheme()

  const [didLoad, setDidLoad] = useState(false);

  const { 
    title, hero_image, hero_image_mob, hero_video, hero_mobile_video, 
    sec1_title, sec1_copy, sec1_sig, sec1_fig_desk, sec1_fig_mob, 
    sec1_fig_caption, makers_title, makers_copy, makers_image, makers_video, 
    makers_mobile_video, collection1, collection1_title, collection1_handle, 
    collection2, collection2_title, collection2_handle 
  } = json.data

  const heroRef = useRef(null);

  const carouTime = {
    timeline: json.data.timeline, 
    title: json.data.timeline_title, 
    copy: json.data.timeline_copy
  }

  const carou1 = {
    carousel: json.data.carousel1,
    title: json.data.carousel1_title,
    copy: json.data.carousel1_copy,
    caption: false
  }

  const carou2 = {
    carousel: json.data.carousel2,
    title: json.data.carousel2_title,
    copy: json.data.carousel2_copy,
    caption: false
  }

  const deskHero = hero_image 
    ? `url(${imgSize(hero_image,1400)})` 
    : undefined;

  const mobiHero = hero_image_mob 
    ? `url(${imgSize(hero_image_mob,900)})`
    : deskHero;

  useEffect(() => {
    if ( !heroRef.current ) return

    heroRef.current.style.backgroundImage = isMobile ? mobiHero : deskHero
  }, [isMobile, deskHero, mobiHero])

  const [mobiHeight, setMobiHeight] = useState(null)
  const [open, setOpen] = useState(true)
  const wrapRef = useRef(null)

  useEffect(() => {
    setDidLoad(true)
    setOpen(false)
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  useEffect(() => {
    if (!wrapRef) return

    let height = mobiHeight
    if (!mobiHeight) {
      height = wrapRef.current.getBoundingClientRect().height + 50
      setMobiHeight(height)
    }
    wrapRef.current.style.maxHeight = open ? `${height}px` : '22em'

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])


  return (
    <div className={styles.factory} bmkm="">
      <div 
        className={styles.hero} 
        data-desk={deskHero}
        data-mob={hero_image_mob ? `url(${imgSize(hero_image_mob,900)})` : undefined}
        ref={heroRef}
      >
        {(didLoad && !isMobile && notBlank(hero_video)) && (
          <VideoControls 
            handle={hero_video}
            par_styles={`${styles.video}`}
            loopAuto={true}
          />
        )}
        {(didLoad && isMobile && notBlank(hero_mobile_video)) && (
          <VideoControls 
            handle={hero_mobile_video}
            par_styles={`${styles.video}`}
            loopAuto={true}
          />
        )}
        {(isMobile && hero_image_mob && !notBlank(hero_video)) && (
          <img 
            src={imgSize(hero_image_mob,900)}
            alt="The Buck Mason factory in Mohnton, PA" 
          />
        )}
        
        <h1 
          className={`${styles.titles} ${notBlank(hero_video) ? styles.title_video : ''}`} 
          dangerouslySetInnerHTML={{ __html: title }} 
        />
      </div>

      {notBlank(sec1_copy) && (
        <div className={[styles.sec, styles.cont, styles.bordered].join(' ')}>
          {notBlank(sec1_title) && (
            <h2 
              className={styles.titles} 
              dangerouslySetInnerHTML={{ __html: sec1_title }} 
            />
          )}

          {notBlank(sec1_copy) && (
            <div className={styles.bodycopy} ref={wrapRef}>
              <div dangerouslySetInnerHTML={{ __html: sec1_copy }} />
              {notBlank(sec1_sig) && (
                <img className={styles.sig} src={sec1_sig} alt="Erik and Sasha, Buck Mason founders" />
              )}
              {!open && (
                <div className={styles.moretxt_wr}>
                  <button 
                    type="button"
                    className={jnstyls([styles.moretxt])}
                    onClick={(e)=> setOpen(!open)}
                  >
                    READ MORE
                  </button>
                </div>
              )}
            </div>
          )}

          {(notBlank(hero_image) && notBlank(sec1_fig_mob)) && (
            <figure className={[styles.fig1, styles.fig].join(' ')}>
              {notBlank(sec1_fig_desk) && (
                <img 
                  className={[styles.img, styles.desk].join(' ')} 
                  src={imgSize(sec1_fig_desk,1100)} 
                  alt=""
                />
              )}
              {notBlank(sec1_fig_mob) && (
                <img className={[styles.img, styles.mobi].join(' ')} src={imgSize(sec1_fig_mob,900)} alt="" />
              )}
              <figcaption 
                className={styles.figcap}
                dangerouslySetInnerHTML={{ __html: sec1_fig_caption }} 
              />
            </figure>
          )}
        </div>
      )}

      {(didLoad && (collection1.length > 0 || collection2.length > 0)) && (
        <div className={[styles.sec, styles.prods].join(' ')}>
          {collection1.length > 0 && (
            <div className={styles.cont}>
              <h2 
                className={styles.titles} 
                dangerouslySetInnerHTML={{ __html: collection1_title }} 
              />

              <div 
                className={tiles}
                style={{width: '100%'}}
              >
                {collection1.map( (product,index) => 
                  <TilePage key={product.handle} product={product} tile_ind={0}  />
                )}
              </div>

              <div className={styles.more}>
                <Flink href={`/collections/${collection1_handle}`} className={`${aria_btn} ${styles.aria_btn}`}>
                  View <span></span> Men's Tees
                </Flink>
              </div> 
            </div>
          )}
          {collection2.length > 0 && (
            <div className={styles.cont}>
              <h2 className={styles.titles}>
                {collection2_title}
              </h2>

              <div 
                className={tiles}
                style={{width: '100%'}}
              >
                {collection2.map( (product,index) => 
                  <TilePage key={product.handle} product={product} tile_ind={0}  />
                )}
              </div>

              <div className={styles.more}>
                <Flink href={`/collections/${collection2_handle}`} className={`${aria_btn} ${styles.aria_btn}`}>
                  View <span></span> Women's Tees
                </Flink>
              </div> 
            </div>
          )}
        </div>
      )}
      <br/>
      <br/>
      <br/>
      <br/>
      <br/>

      {(didLoad && notBlank(json.data.timeline)) && (
        <CarouselTimeline data={carouTime} par_styles={`${styles.sec}`} />
      )}

      {(didLoad && notBlank(json.data.carousel1)) && (
        <CarouselFixed carou_data={carou1} par_styles={`${styles.sec} ${styles.fact_fixed}`} />
      )}
      
      {didLoad && notBlank(makers_copy) && (
        <div className={[styles.sec, styles.makers].join(' ')}>
          <div className={styles.cont}>
            {(!isMobile && notBlank(makers_video)) && (
              <VideoNoControl 
                handle={makers_video}
                par_classes={``}
              />
            )}
            {(isMobile && notBlank(makers_mobile_video)) && (
              <VideoNoControl 
                handle={makers_mobile_video}
                par_classes={``}
              />
            )}
            {(!notBlank(makers_video) && notBlank(makers_image)) && (
              <img className={styles.img} src={imgSize(makers_image,1000)} alt="Worker in the Buck Mason factory" />
            )}
            {notBlank(makers_title) && (
              <h2 
                className={styles.titles} 
                dangerouslySetInnerHTML={{ __html: makers_title }} 
              />
            )}
            {notBlank(makers_copy) && (
              <div 
                className={styles.bodycopy} 
                dangerouslySetInnerHTML={{ __html: makers_copy }} 
              />
            )}
          </div>
        </div>
      )}

      {(didLoad && json.data.carousel2 && notBlank(json.data.carousel2)) && (
        <CarouselWindow carou_data={carou2} par_styles={`${styles.sec} ${styles.cont} ${styles.bordered} ${styles.desk_last}`} />
      )}

    </div>
  )
}

export default Factory



export const Head = ({ data: { json } }) => (
  <Seo 
    title={'Buck Mason Knitting Mills'}
    description={`The Mohnton mill plays an essential part in the rich history of textile manufacturing and 
    millinery in Eastern Pennsylvania, dating back to its opening in 1873. This is where the majority of 
    American clothing was once made with pride. And now we’re bringing that integrity and dignity back 
    full circle.`}
    image={json.data.metaimg}
    url={`/pages/buck-mason-knitting-mills/`}
  />
)



export const pageQuery = graphql`
  {
    json: contentPage(type: {eq: "Factory"}) {
      data
    }
  }
`;



